// ── Audit Write-Ahead Queue ───────────────────────────────────────────────────
//
// All audit events are written to localStorage FIRST (synchronous, always works)
// then synced to Supabase in the background.
//
// An event has one of three states:
//   pending   — written locally, not yet attempted
//   syncing   — currently being sent to Supabase
//   failed    — attempted but Supabase returned an error (will retry)
//
// The queue drains automatically when:
//   - The browser comes back online
//   - The sync interval fires (every 30s)
//   - syncQueue() is called manually

import { supabase } from './supabase';

// Session ID lives here to avoid circular import with supabase.js
const SESSION_KEY = 'vi_session_id';
export const getSessionId = () => {
  let id = sessionStorage.getItem(SESSION_KEY);
  if (!id) {
    id = 'sess_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
    sessionStorage.setItem(SESSION_KEY, id);
  }
  return id;
};

const QUEUE_KEY = 'vi_audit_queue';
const SYNC_INTERVAL_MS = 30000; // 30 seconds
const MAX_RETRY_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days — matches Supabase log retention

// ── Queue storage ─────────────────────────────────────────────────────────────

export const readQueue = () => {
  try {
    return JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]');
  } catch {
    return [];
  }
};

const writeQueue = (events) => {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(events));
  } catch (err) {
    // localStorage full — extremely rare but handle gracefully
    console.error('Audit queue storage failed:', err);
  }
};

export const getPendingCount = () =>
  readQueue().filter(e => e.state === 'pending' || e.state === 'failed').length;

// ── Enqueue ───────────────────────────────────────────────────────────────────

// Write an event to the local queue. Synchronous — always succeeds.
// Returns the queued event with its local ID.
export const enqueueAuditEvent = ({
  inspectionId,
  eventType,
  step = null,
  field = null,
  previousValue = null,
  newValue = null,
  metadata = {},
}) => {
  if (!inspectionId) return null;

  const event = {
    // Local queue metadata
    localId: 'audit_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 6),
    state: 'pending',
    enqueuedAt: new Date().toISOString(),
    attempts: 0,
    lastAttemptAt: null,

    // Supabase record
    inspection_id: inspectionId,
    event_type: eventType,
    step,
    field,
    previous_value: previousValue ? JSON.parse(JSON.stringify(previousValue)) : null,
    new_value: newValue ? JSON.parse(JSON.stringify(newValue)) : null,
    session_id: getSessionId(),
    user_agent: navigator.userAgent,
    metadata,
    // org_id is set by looking up the inspection's org at sync time
    // to avoid needing to pass it through every call site
    org_id: null, // populated by syncQueue via inspection lookup
  };

  const queue = readQueue();
  queue.push(event);
  writeQueue(queue);

  return event;
};

// ── Sync ──────────────────────────────────────────────────────────────────────

let syncInProgress = false;

// Attempt to flush all pending/failed events to Supabase.
// Returns { synced, failed, skipped }
export const syncQueue = async () => {
  if (syncInProgress) return { synced: 0, failed: 0, skipped: 0 };
  if (!navigator.onLine) return { synced: 0, failed: 0, skipped: 0 };

  syncInProgress = true;
  let synced = 0, failed = 0, skipped = 0;

  try {
    const queue = readQueue();
    const toSync = queue.filter(e => e.state === 'pending' || e.state === 'failed');

    if (toSync.length === 0) {
      syncInProgress = false;
      return { synced: 0, failed: 0, skipped: 0 };
    }

    // Mark all as syncing so UI can reflect this
    const updated = queue.map(e =>
      toSync.find(t => t.localId === e.localId)
        ? { ...e, state: 'syncing' }
        : e
    );
    writeQueue(updated);

    // Notify listeners that queue state changed
    window.dispatchEvent(new CustomEvent('auditQueueUpdate'));

    // Attempt each event
    const results = await Promise.allSettled(
      toSync.map(async (event) => {
        // Drop events older than retention window — they'd be rejected anyway
        const age = Date.now() - new Date(event.enqueuedAt).getTime();
        if (age > MAX_RETRY_AGE_MS) {
          return { localId: event.localId, result: 'expired' };
        }

        const { localId, state, enqueuedAt, attempts, lastAttemptAt, ...record } = event;

        // Populate org_id from the inspection if not already set
        if (!record.org_id && record.inspection_id) {
          const { data: insp } = await supabase
            .from('vehicle_inspections')
            .select('org_id')
            .eq('id', record.inspection_id)
            .single();
          if (insp?.org_id) record.org_id = insp.org_id;
        }

        const { error } = await supabase.from('inspection_audit_log').insert(record);

        if (error) throw Object.assign(new Error(error.message || 'Supabase error'), { localId: event.localId, originalError: error });
        return { localId: event.localId, result: 'synced' };
      })
    );

    // Update queue based on results
    const finalQueue = readQueue().map(e => {
      const match = results.find(r =>
        r.status === 'fulfilled' && r.value.localId === e.localId
      );
      const failure = results.find(r =>
        r.status === 'rejected' && r.reason.localId === e.localId
      );
      const expired = results.find(r =>
        r.status === 'fulfilled' && r.value.result === 'expired' && r.value.localId === e.localId
      );

      if (match || expired) {
        synced++;
        return null; // Remove from queue
      }
      if (failure) {
        failed++;
        return {
          ...e,
          state: 'failed',
          attempts: e.attempts + 1,
          lastAttemptAt: new Date().toISOString(),
          lastError: failure.reason.error?.message || 'Unknown error',
        };
      }
      return e;
    }).filter(Boolean);

    writeQueue(finalQueue);
    window.dispatchEvent(new CustomEvent('auditQueueUpdate'));

  } finally {
    syncInProgress = false;
  }

  return { synced, failed, skipped };
};

// ── Background sync engine ────────────────────────────────────────────────────

let syncInterval = null;

export const startSyncEngine = () => {
  // Sync immediately on start
  syncQueue();

  // Sync whenever connectivity returns
  window.addEventListener('online', () => {
    console.log('Connectivity restored — syncing audit queue');
    syncQueue();
  });

  // Sync on a regular interval as a fallback
  if (!syncInterval) {
    syncInterval = setInterval(syncQueue, SYNC_INTERVAL_MS);
  }

  // Sync when app regains focus (inspector switches back from camera etc.)
  window.addEventListener('focus', syncQueue);
};

export const stopSyncEngine = () => {
  if (syncInterval) {
    clearInterval(syncInterval);
    syncInterval = null;
  }
};
